import { DateTime } from 'luxon';
import { SideOrderT } from '../../../../@types/sideOrder.type';
import { ProgramTypeE } from '../../../../@types/orderForm.type';
import {
  BasketItemT,
  CustomOrderDaysDayT,
} from '../../../../@types/basketItem.type';
import { SimpleOrdersT } from '../../../../features/orderForm/stepSummary/types/stepSummary.type';
import { DeliveryMethodEnum } from '../../../../features/pickupPoint/types/deliveryMethod.enum';

export const mapDeliveryDates = (selectedDays: Array<number>) =>
  selectedDays.map(day => DateTime.fromMillis(day).toISODate());

export const mapSelectedMeals = (
  selectedMeals: Array<{ selected: boolean; dietCaloriesMealId: number }>
) => {
  if (!selectedMeals) {
    return [];
  }

  return selectedMeals?.reduce(
    (
      acc: Array<{
        amount: number;
        dietCaloriesMealId: number;
      }>,
      curr
    ) => {
      const isSelected = curr.selected;
      if (isSelected) {
        acc.push({
          amount: 1,
          dietCaloriesMealId: curr.dietCaloriesMealId,
        });
      }
      return acc;
    },
    []
  );
};

type Day = {
  mealsList: Record<
    string,
    {
      list: Array<{ dietCaloriesMealId: number; selected: boolean }>;
      selected: boolean;
    }
  >;
  sideOrders: Array<{
    amount: number;
    possibleSideOrderId: number;
  }>;
};

type Days = {
  [key: string]: Day;
};

export const generateCustomDeliveryMeals = (
  selectedProgram: ProgramTypeE,
  days: Record<string, CustomOrderDaysDayT>
): Record<string, Array<{ amount: number; dietCaloriesMealId: number }>> => {
  if (selectedProgram !== ProgramTypeE.MenuConfiguration) {
    return {};
  }

  const checkIsCustomDeliveryMealsNotEmpty = Object.values(days).some(item => {
    const mealsList = Object.values(item.mealsList);

    return !!mealsList.length && mealsList.some(meal => !!meal.list.length);
  });

  if (!checkIsCustomDeliveryMealsNotEmpty) {
    return {};
  }

  const daysKeys = Object.keys(days);

  return daysKeys.reduce((accDay, curDay) => {
    const mealsListValues = Object.values(days[curDay]?.mealsList || {})
      .filter(item => item.selected)
      .map(selectedItem =>
        selectedItem.list.find(listItem => listItem.selected)
      )
      .reduce((acc, cur) => {
        if (cur) {
          const meal = acc[cur.dietCaloriesMealId];

          if (meal) {
            meal.amount = ++meal.amount;
          } else {
            acc[cur.dietCaloriesMealId] = {
              amount: 1,
              dietCaloriesMealId: cur.dietCaloriesMealId,
            };
          }
        }

        return acc;
      }, {} as Record<number, { amount: number; dietCaloriesMealId: number }>);

    if (Object.values(mealsListValues).length) {
      accDay[curDay] = Object.values(mealsListValues || {});
    }

    return accDay;
  }, {} as Record<string, Array<{ amount: number; dietCaloriesMealId: number }>>);
};

type SideOrder = {
  multiplier: number;
  possibleSideOrderId: number;
  deliveryDates: Array<string>;
};

export const generateSideOrdersList = (
  days: Days = {},
  selectedSideOrders: Array<SideOrderT> | [],
  selectedDays: Array<number>
): Array<SideOrder> => {
  const parsedSelectedDays = mapDeliveryDates(selectedDays);

  const combinedSideOrders: { [key: string]: SideOrder } = {};

  for (const [key, value] of Object.entries(days)) {
    const sideOrdersList = value.sideOrders.filter(item => item.amount);

    sideOrdersList.forEach(sideOrder => {
      const deliveryDates = [key];
      const existingSideOrder =
        combinedSideOrders[
          `${sideOrder.possibleSideOrderId}-${sideOrder.amount}`
        ];

      if (existingSideOrder) {
        existingSideOrder.deliveryDates.push(...deliveryDates);
      } else {
        combinedSideOrders[
          `${sideOrder.possibleSideOrderId}-${sideOrder.amount}`
        ] = {
          multiplier: sideOrder.amount,
          possibleSideOrderId: sideOrder.possibleSideOrderId,
          deliveryDates,
        };
      }
    });
  }

  selectedSideOrders.forEach(sideOrder => {
    const deliveryDates = parsedSelectedDays.filter(
      // TODO ANY
      (element: any) => !Object.keys(days).includes(element)
      // TODO ANY
    ) as Array<any>;

    const existingSideOrder =
      combinedSideOrders[
        `${sideOrder.possibleSideOrderId}-${sideOrder.amount}`
      ];

    if (existingSideOrder) {
      existingSideOrder.deliveryDates.push(...deliveryDates);
    } else {
      combinedSideOrders[
        `${sideOrder.possibleSideOrderId}-${sideOrder.amount}`
      ] = {
        multiplier: sideOrder.amount,
        possibleSideOrderId: sideOrder.possibleSideOrderId,
        deliveryDates,
      };
    }
  });

  return Object.values(combinedSideOrders);
};

export const generateSimpleOrders = (data: any, basket: Array<BasketItemT>) => {
  const simpleOrders: Array<SimpleOrdersT> = [];

  basket.forEach(item => {
    const {
      mealExchange,
      orderId,
      selectedCalories,
      selectedDays,
      selectedMeals,
      selectedProgram,
      selectedSideOrders,
      sets,
      testOrder,
    } = item;

    const pickupPointId =
      data.deliveryMethod === DeliveryMethodEnum.P ? data.pickupPointId : null;

    const profileAddressId =
      data.methodOfPurchase === 'login' &&
      data.selectedProfileAddress?.profileAddressId
        ? data.selectedProfileAddress.profileAddressId
        : null;

    const deliveryDates = mapDeliveryDates(selectedDays) as Array<string>;
    const customDeliveryMeals = generateCustomDeliveryMeals(
      selectedProgram,
      mealExchange?.days as any
    );
    const deliveryMeals = mapSelectedMeals(selectedMeals);
    const sideOrders = generateSideOrdersList(
      mealExchange.days as any,
      selectedSideOrders,
      selectedDays
    );

    for (let i = 0; i < sets; i++) {
      simpleOrders.push({
        itemId: orderId,
        addressId: null,
        deliveryDates,
        customDeliveryMeals,
        deliveryMeals,
        dietCaloriesId: selectedCalories.dietCaloriesId,
        hourPreference: data.deliveryHour,
        invoice: data.company,
        note: data.note,
        paymentType: data.paymentType,
        pickupPointId,
        profileAddressId,
        sideOrders,
        testOrder,
      });
    }
  });

  return simpleOrders;
};
